import { useState, useEffect, useCallback, useRef } from 'react';
import { SelectChangeEvent } from '@mui/material';
import {
  Product,
  ProductFilter,
  PaginationState,
  ProductTag,
  ProductCatalogSortField,
  DEFAULT_PRODUCT_CATALOG_SORT,
} from '../../../types/product.types';
import { usePageState } from '../../../hooks/usePageState';
import { ProductsService } from '../../../api';
import { DeleteResult } from '../../modals/confirmation';
import { normalizeTags, tagArrayToIdList } from '../../../utils/helpers';
import { useProductMetadata } from '../../../hooks';
import { PRODUCT_MESSAGES } from '../../../utils/constants';

// Interfaz para el estado persistente
interface ProductCatalogState {
  filters: ProductFilter;
  showMoreFilters: boolean;
  pagination: PaginationState;
  selectedProducts: number[];
  productSort: { sortBy: ProductCatalogSortField; sortOrder: 'asc' | 'desc' };
}

const emptyProductFilters = (): ProductFilter => ({
  searchTerm: '',
  productType: '',
  productStatus: '',
  supplierId: '',
  searchTag: '',
  minWeight: undefined,
  maxWeight: undefined,
  materialId: '',
  diameterSize: '',
  color: '',
  workshopCode: '',
});

export interface UseProductCatalogReturn {
  // Filter states
  filters: ProductFilter;
  setFilters: React.Dispatch<React.SetStateAction<ProductFilter>>;
  appliedFilters: ProductFilter;
  showMoreFilters: boolean;
  setShowMoreFilters: React.Dispatch<React.SetStateAction<boolean>>;
  handleClearAppliedField: (patch: Partial<ProductFilter>) => void;

  // Pagination states
  pagination: PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;

  // Drawer states
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedProduct: Product | null;
  setSelectedProduct: React.Dispatch<React.SetStateAction<Product | null>>;
  /** When set, ProductDrawer opens prefilled for a new product (no id/code). */
  duplicateSourceProduct: Product | null;
  setDuplicateSourceProduct: React.Dispatch<React.SetStateAction<Product | null>>;

  // Modal states
  viewModalOpen: boolean;
  setViewModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  viewProduct: any | null;
  setViewProduct: React.Dispatch<React.SetStateAction<any | null>>;
  deleteModalOpen: boolean;
  setDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  productsToDelete: Product[];
  setProductsToDelete: React.Dispatch<React.SetStateAction<Product[]>>;

  // Product data
  products: Product[];
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  selectedProducts: number[];
  setSelectedProducts: React.Dispatch<React.SetStateAction<number[]>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;

  // Product metadata
  productTypes: { id: number; name: string }[];
  productTags: ProductTag[];
  productMaterials: { id: number; name: string }[];
  productColors: { id: number; name: string }[];
  productSuppliers: { id: number; name: string }[];

  // Notification state
  notification: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
    successMessage?: string;
    errorMessage?: string;
  };
  setNotification: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      message: string;
      severity: 'success' | 'error' | 'warning' | 'info';
    }>
  >;

  // Handlers
  handleSearch: () => void;
  handleReset: () => void;
  handleFilterChange: (newFilters: Partial<ProductFilter>) => void;
  handlePaginationChange: (newPagination: Partial<PaginationState>) => void;
  handleItemsPerPageChange: (event: SelectChangeEvent<unknown>) => void;
  handleProductSelect: (productId: number, selected: boolean) => void;
  handleSelectAll: (selected: boolean) => void;
  handleAddProduct: () => void;
  handleDuplicateProduct: (product: Product) => void;
  handleEditProduct: (product: Product) => void;
  handleViewProduct: (product: Product) => void;
  handleDeleteProducts: () => Promise<void>;
  handleDeleteSingleProduct: (id: number) => Promise<void>;
  handleConfirmDelete: () => Promise<DeleteResult[]>;
  handleCloseDeleteModal: () => void;
  handleDeleteComplete: (results: DeleteResult[]) => void;
  handleCloseDrawer: () => void;
  handleSaveProduct: (product: any) => Promise<void>;
  handleCloseViewModal: () => void;
  handleCloseNotification: () => void;
  handleExportProducts: () => void;
  handleImportProducts: () => void;
  handleCategoryAdded: (newCategoryId: number) => Promise<void>;
  handleTagAdded: (newTagId: number) => Promise<void>;
  handleMaterialAdded: (newMaterialId: number) => Promise<void>;
  handleColorAdded: (newColorId: number) => Promise<void>;
  handleSupplierAdded: (newSupplierId: number) => Promise<void>;

  productSort: { sortBy: ProductCatalogSortField; sortOrder: 'asc' | 'desc' };
  handleProductSortRequest: (sortField: string) => void;
}

export const useProductCatalog = (): UseProductCatalogReturn => {
  // Estado persistente de la página
  const { state, saveState } = usePageState<ProductCatalogState>();

  // Draft inputs vs applied (chips + fetch)
  const initialFilters = state.filters || emptyProductFilters();
  const [filters, setFilters] = useState<ProductFilter>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<ProductFilter>(initialFilters);
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(state.showMoreFilters || false);
  const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
  const [viewProduct, setViewProduct] = useState<any | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [productsToDelete, setProductsToDelete] = useState<Product[]>([]);

  // Pagination states - inicializado desde el estado persistente o con valores por defecto
  const [pagination, setPagination] = useState<PaginationState>(
    state.pagination || {
      itemsPerPage: 10,
      currentPage: 1,
      totalItems: 0,
    },
  );

  const [productSort, setProductSort] = useState<{
    sortBy: ProductCatalogSortField;
    sortOrder: 'asc' | 'desc';
  }>(state.productSort ?? DEFAULT_PRODUCT_CATALOG_SORT);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [duplicateSourceProduct, setDuplicateSourceProduct] = useState<Product | null>(null);

  // Product data
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<number[]>(state.selectedProducts || []);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Product metadata
  const {
    tags: productTags,
    productTypes,
    productMaterials,
    productColors,
    productSuppliers,
    refreshTags,
    refreshTypes,
    refreshMaterials,
    refreshColors,
    refreshSuppliers,
  } = useProductMetadata();

  // Notification state
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
    successMessage?: string;
    errorMessage?: string;
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Add a search trigger state to control when searches are executed
  const [searchTrigger, setSearchTrigger] = useState(0);

  /** Un solo POST/PUT por operación de guardado (anti doble clic). */
  const saveInFlightRef = useRef(false);

  // Guardar el estado cuando cambie
  useEffect(() => {
    saveState({
      filters,
      showMoreFilters,
      pagination,
      selectedProducts,
      productSort,
    });
  }, [filters, showMoreFilters, pagination, selectedProducts, productSort, saveState]);

  // Fetch metadata method
  const fetchMetadata = useCallback(async () => {
    try {
      await Promise.all([
        refreshTypes(),
        refreshTags(),
        refreshMaterials(),
        refreshColors(),
        refreshSuppliers(),
      ]);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  }, [refreshTypes, refreshTags, refreshMaterials, refreshColors, refreshSuppliers]);

  // Fetch products and metadata - only when searchTrigger changes or pagination changes
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTrigger,
    pagination.currentPage,
    pagination.itemsPerPage,
    productSort.sortBy,
    productSort.sortOrder,
  ]);

  // Fetch metadata only once on mount
  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  // Initial search trigger
  useEffect(() => {
    setSearchTrigger(1);
  }, []);

  // Fetch products method
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ProductsService.productsControllerFindAll(
        pagination.currentPage,
        pagination.itemsPerPage,
        undefined, // searchName - no longer used
        undefined, // searchCode - no longer used
        appliedFilters.searchTerm || undefined, // searchTerm - new combined search
        appliedFilters.productType ? parseInt(appliedFilters.productType) : undefined,
        (appliedFilters.productStatus as any) || undefined,
        appliedFilters.supplierId ? parseInt(appliedFilters.supplierId) : undefined,
        appliedFilters.minWeight || undefined,
        appliedFilters.maxWeight || undefined,
        appliedFilters.searchTag || undefined, // searchTag - new tag search
        appliedFilters.materialId ? parseInt(appliedFilters.materialId) : undefined, // materialId
        appliedFilters.diameterSize || undefined, // diameterSize
        appliedFilters.color || undefined, // color
        appliedFilters.workshopCode || undefined, // workshopCode
        productSort.sortBy,
        productSort.sortOrder === 'asc' ? 'ASC' : 'DESC',
      );

      if (response && response.data) {
        // Debug: ensure we are using the correct total for pagination
        // console.log('Products total candidates:', {
        //   total: (response as any)?.total,
        //   paginationTotal: (response as any)?.pagination?.total,
        //   dataLength: response.data?.length
        // });
        const productsData = response.data.map((p: any) => ({
          id: p.id,
          code: p.code,
          name: p.name,
          description: p.description || '',
          status: p.status,
          type: p.category?.name || p.type || '',
          categoryId: p.category?.id || p.categoryId || null,
          material: p.material?.name || p.material || '',
          materialId: p.material?.id || p.materialId || null,
          weight: (p.weightGrams ?? p.weight ?? '')?.toString() || '',
          length:
            p.lengthMm != null
              ? (Number(p.lengthMm) / 10).toString()
              : (p.length ?? '')?.toString() || '',
          diameter: p.diameterSize || p.diameter || '',
          color: p.color || '',
          workshopCode: p.workshopCode || '',
          costPrice: (p.costPrice ?? '')?.toString() || '',
          sellingPrice: (p.salePrice ?? p.sellingPrice ?? '')?.toString() || '',
          supplier: p.supplier?.name || p.supplier || '',
          supplierId: p.supplier?.id || p.supplierId || null,
          tags: normalizeTags(p.tags),
          imagePath: p.imagePath || '',
          hasThumbnail: p.hasThumbnail === true,
          thumbnailUrl: p.thumbnailUrl || null,
          price: p.salePrice != null ? p.salePrice.toString() : '',
          createdAt: p.createdAt ?? undefined,
          updatedAt: p.updatedAt ?? undefined,
        }));
        setProducts(productsData);
        const totalItems =
          (response as any).meta?.total ??
          response.total ??
          (response as any).pagination?.total ??
          productsData.length;
        setPagination((prev) => ({
          ...prev,
          totalItems,
        }));
      } else {
        setProducts([]);
        setPagination((prev) => ({
          ...prev,
          totalItems: 0,
        }));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products. Please try again later.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.currentPage,
    pagination.itemsPerPage,
    appliedFilters,
    productSort.sortBy,
    productSort.sortOrder,
  ]);

  // Event handlers
  const handleSearch = useCallback(() => {
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, [filters]);

  const handleReset = useCallback(() => {
    const empty = emptyProductFilters();
    setFilters(empty);
    setAppliedFilters(empty);
    setProductSort(DEFAULT_PRODUCT_CATALOG_SORT);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const handleClearAppliedField = useCallback((patch: Partial<ProductFilter>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setAppliedFilters((prev) => ({ ...prev, ...patch }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const handleProductSortRequest = useCallback((sortField: string) => {
    const field = sortField as ProductCatalogSortField;
    setProductSort((prev) => {
      if (prev.sortBy === field) {
        return {
          sortBy: field,
          sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
        };
      }
      return { sortBy: field, sortOrder: 'asc' };
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleFilterChange = useCallback((newFilters: Partial<ProductFilter>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const handlePaginationChange = useCallback((newPagination: Partial<PaginationState>) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  }, []);

  const handleItemsPerPageChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const itemsPerPage = parseInt(event.target.value as string);
      handlePaginationChange({ itemsPerPage, currentPage: 1 });
    },
    [handlePaginationChange],
  );

  const handleProductSelect = useCallback((productId: number, selected: boolean) => {
    setSelectedProducts((prev) =>
      selected ? [...prev, productId] : prev.filter((id) => id !== productId),
    );
  }, []);

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedProducts(products.map((p) => p.id));
      } else {
        setSelectedProducts([]);
      }
    },
    [products],
  );

  const handleAddProduct = useCallback(() => {
    setDuplicateSourceProduct(null);
    setSelectedProduct(null);
    setDrawerOpen(true);
  }, []);

  const handleDuplicateProduct = useCallback((product: Product) => {
    setSelectedProduct(null);
    setDuplicateSourceProduct(product);
    setDrawerOpen(true);
  }, []);

  const handleEditProduct = useCallback((product: Product) => {
    setDuplicateSourceProduct(null);
    setSelectedProduct(product);
    setDrawerOpen(true);
  }, []);

  const handleViewProduct = useCallback((product: Product) => {
    setViewProduct(product);
    setViewModalOpen(true);
  }, []);

  const handleDeleteProducts = useCallback(async () => {
    if (selectedProducts.length === 0) return;

    // Get the products to delete
    const productsToDeleteList = products.filter((p) => selectedProducts.includes(p.id));
    setProductsToDelete(productsToDeleteList);
    setDeleteModalOpen(true);
  }, [selectedProducts, products]);

  const handleDeleteSingleProduct = useCallback(
    async (id: number) => {
      const product = products.find((p) => p.id === id);
      if (!product) return;

      setProductsToDelete([product]);
      setDeleteModalOpen(true);
    },
    [products],
  );

  const handleConfirmDelete = useCallback(async (): Promise<DeleteResult[]> => {
    const productIds = productsToDelete.map((p) => p.id);

    if (productIds.length === 0) {
      return [];
    }

    try {
      if (productIds.length === 1) {
        // Single delete
        try {
          await ProductsService.productsControllerRemove(productIds[0]);
          setSearchTrigger((prev) => prev + 1); // Refresh the list
          setSelectedProducts([]);
          return [
            {
              productId: productIds[0],
              success: true,
            },
          ];
        } catch (error) {
          console.error('Error deleting single product:', error);

          // Parse error response for single delete
          let errorDetails = {
            reason: 'Error al eliminar el producto',
            errorCode: 0,
            countAssociates: undefined as Array<number> | undefined,
            associationTypes: undefined as Array<string> | undefined,
          };

          if (error && typeof error === 'object') {
            const apiError = error as any;
            if (apiError.body) {
              errorDetails = {
                reason: apiError.body.message || 'Error al eliminar el producto',
                errorCode: apiError.body.errorCode || 0,
                countAssociates: apiError.body.countAssociates,
                associationTypes: apiError.body.associationTypes,
              };
            } else if (apiError.message) {
              errorDetails.reason = apiError.message;
            }
          }

          return [
            {
              productId: productIds[0],
              success: false,
              error: errorDetails,
            },
          ];
        }
      } else {
        // Bulk delete
        const response = await ProductsService.productsControllerBulkRemove({
          productIds,
        });

        setSearchTrigger((prev) => prev + 1); // Refresh the list
        setSelectedProducts([]);

        // Transform bulk delete response to DeleteResult[]
        const results: DeleteResult[] = productIds.map((id) => {
          const error = response.errors?.find((e) => e.productId === id);
          if (error) {
            return {
              productId: id,
              success: false,
              error: {
                reason: error.reason || 'Error desconocido',
                errorCode: error.errorCode || 0,
                countAssociates: error.countAssociates,
                associationTypes: error.associationTypes,
              },
            };
          }
          return {
            productId: id,
            success: true,
          };
        });

        return results;
      }
    } catch (error) {
      console.error('Error deleting products:', error);

      // Return error for all products
      return productIds.map((id) => ({
        productId: id,
        success: false,
        error: {
          reason: 'Error inesperado al eliminar el producto',
          errorCode: 0,
          countAssociates: undefined,
          associationTypes: undefined,
        },
      }));
    }
  }, [productsToDelete]);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setProductsToDelete([]);
  }, []);

  const handleDeleteComplete = useCallback(
    (results: DeleteResult[]) => {
      const successCount = results.filter((r) => r.success).length;
      const errorCount = results.filter((r) => !r.success).length;

      if (errorCount === 0) {
        // Todos exitosos
        setNotification({
          open: true,
          message: `Todos los productos (${successCount}) se eliminaron correctamente`,
          severity: 'success',
        });
      } else if (successCount === 0) {
        // Todos fallaron
        setNotification({
          open: true,
          message: `No se pudo eliminar ${errorCount} producto(s)`,
          severity: 'error',
        });
      } else {
        // Hay éxitos y errores mezclados - mostrar ambos mensajes simultáneamente
        const successMsg =
          successCount === 1
            ? `1 producto se eliminó correctamente`
            : `${successCount} productos se eliminaron correctamente`;
        const errorMsg =
          errorCount === 1
            ? `1 producto no se pudo eliminar`
            : `${errorCount} productos no se pudieron eliminar`;

        setNotification({
          open: true,
          message: '', // No usamos message cuando hay múltiples
          severity: 'info', // No importa cuando hay múltiples
          successMessage: successMsg,
          errorMessage: errorMsg,
        });
      }
    },
    [setNotification],
  );

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedProduct(null);
    setDuplicateSourceProduct(null);
  }, []);

  const handleSaveProduct = useCallback(
    async (product: any) => {
      if (saveInFlightRef.current) {
        return;
      }
      saveInFlightRef.current = true;

      try {
        // Use tagIds if already provided (from drawer with newly created tags)
        // Otherwise convert tag names to tag IDs
        let tagIds = product.tagIds;
        if (tagIds === undefined && product.tags) {
          tagIds = tagArrayToIdList(normalizeTags(product.tags));
        }
        if (tagIds === undefined) {
          tagIds = [];
        }

        // Build API payload (convert UI strings to API fields/ids)
        const payload: any = {
          ...(product.id ? { code: product.code } : {}),
          name: product.name,
          description: product.description || undefined,
          status: product.status,
          categoryId: product.type ? parseInt(product.type) : undefined,
          materialId: product.material ? parseInt(product.material) : undefined,
          weightGrams: product.weight ? parseFloat(product.weight) : undefined,
          lengthMm: product.length ? parseFloat(product.length) * 10 : undefined,
          diameterSize: product.diameter || undefined,
          colorId: product.colorId ? parseInt(product.colorId) : undefined,
          color: product.color || undefined,
          workshopCode: product.workshopCode || undefined,
          costPrice: product.costPrice ? parseFloat(product.costPrice) : undefined,
          salePrice: product.sellingPrice ? parseFloat(product.sellingPrice) : undefined,
          supplierId: product.supplier ? parseInt(product.supplier) : undefined,
          imagePath: product.imagePath || undefined,
          ...(product.imagePath ? { hasThumbnail: product.hasThumbnail === true } : {}),
          tagIds,
        };

        if (product.id) {
          await ProductsService.productsControllerUpdate(product.id.toString(), payload);
          setNotification({
            open: true,
            ...PRODUCT_MESSAGES.UPDATED_SUCCESS,
          });
        } else {
          await ProductsService.productsControllerCreate(payload);
          setNotification({
            open: true,
            ...PRODUCT_MESSAGES.CREATED_SUCCESS,
          });
        }

        setDrawerOpen(false);
        setSelectedProduct(null);
        setDuplicateSourceProduct(null);

        // Refresh metadata to get any new categories/materials/suppliers created
        await fetchMetadata();

        setSearchTrigger((prev) => prev + 1); // Refresh the list
      } catch (error) {
        console.error('Error saving product:', error);
        setNotification({
          open: true,
          ...PRODUCT_MESSAGES.SAVE_ERROR,
        });
        throw error;
      } finally {
        saveInFlightRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productTags, fetchMetadata],
  );

  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setViewProduct(null);
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification({
      open: false,
      message: '',
      severity: 'info',
      successMessage: undefined,
      errorMessage: undefined,
    });
  }, []);

  const handleExportProducts = useCallback(async () => {
    try {
      setNotification({
        open: true,
        ...PRODUCT_MESSAGES.EXPORTING,
      });

      // Make a direct axios call with responseType: 'blob'
      const axios = (await import('axios')).default;
      const { OpenAPI } = await import('../../../api');

      // Get token from OpenAPI config (it's a function)
      const token =
        typeof OpenAPI.TOKEN === 'function' ? await OpenAPI.TOKEN({} as any) : OpenAPI.TOKEN;

      const response = await axios.get(`${OpenAPI.BASE}/products/export/csv`, {
        responseType: 'blob',
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      // Create a download link for the blob
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      link.download = `products_export_${date}.csv`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setNotification({
        open: true,
        ...PRODUCT_MESSAGES.EXPORTED_SUCCESS,
      });
    } catch (error) {
      console.error('Error exporting products:', error);
      setNotification({
        open: true,
        ...PRODUCT_MESSAGES.EXPORT_ERROR,
      });
    }
  }, []);

  const handleImportProducts = useCallback(() => {
    // TODO: Implement import functionality
    setNotification({
      open: true,
      ...PRODUCT_MESSAGES.IMPORT_NOT_IMPLEMENTED,
    });
  }, []);

  const handleCategoryAdded = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (newCategoryId: number) => {
      // Refresh categories list after adding a new one
      try {
        await fetchMetadata();
      } catch (error) {
        console.error('Error refreshing categories:', error);
      }
    },
    [fetchMetadata],
  );

  const handleTagAdded = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (newTagId: number) => {
      // Refresh tags list after adding a new one
      try {
        await fetchMetadata();
      } catch (error) {
        console.error('Error refreshing tags:', error);
      }
    },
    [fetchMetadata],
  );

  const handleMaterialAdded = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (newMaterialId: number) => {
      // Refresh materials list after adding a new one
      try {
        await fetchMetadata();
      } catch (error) {
        console.error('Error refreshing materials:', error);
      }
    },
    [fetchMetadata],
  );

  const handleColorAdded = useCallback(
    async (newColorId: number) => {
      // Keep shared metadata in sync after creating a color from drawer
      if (newColorId > 0) {
        await fetchMetadata();
      }
    },
    [fetchMetadata],
  );

  const handleSupplierAdded = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (newSupplierId: number) => {
      // Refresh suppliers list after adding a new one
      try {
        await fetchMetadata();
      } catch (error) {
        console.error('Error refreshing suppliers:', error);
      }
    },
    [fetchMetadata],
  );

  return {
    // Filter states
    filters,
    setFilters,
    appliedFilters,
    showMoreFilters,
    setShowMoreFilters,
    handleClearAppliedField,

    // Pagination states
    pagination,
    setPagination,

    // Drawer states
    drawerOpen,
    setDrawerOpen,
    selectedProduct,
    setSelectedProduct,
    duplicateSourceProduct,
    setDuplicateSourceProduct,

    // Modal states
    viewModalOpen,
    setViewModalOpen,
    viewProduct,
    setViewProduct,
    deleteModalOpen,
    setDeleteModalOpen,
    productsToDelete,
    setProductsToDelete,

    // Product data
    products,
    setProducts,
    selectedProducts,
    setSelectedProducts,
    loading,
    setLoading,
    error,
    setError,

    // Product metadata
    productTypes,
    productTags,
    productMaterials,
    productColors,
    productSuppliers,

    // Notification state
    notification,
    setNotification,

    // Handlers
    handleSearch,
    handleReset,
    handleFilterChange,
    handlePaginationChange,
    handleItemsPerPageChange,
    handleProductSelect,
    handleSelectAll,
    handleAddProduct,
    handleDuplicateProduct,
    handleEditProduct,
    handleViewProduct,
    handleDeleteProducts,
    handleDeleteSingleProduct,
    handleConfirmDelete,
    handleCloseDeleteModal,
    handleDeleteComplete,
    handleCloseDrawer,
    handleSaveProduct,
    handleCloseViewModal,
    handleCloseNotification,
    handleExportProducts,
    handleImportProducts,
    handleCategoryAdded,
    handleTagAdded,
    handleMaterialAdded,
    handleColorAdded,
    handleSupplierAdded,

    productSort,
    handleProductSortRequest,
  };
};
